/**
 * Batch status polling hook
 * 
 * This hook provides automatic batch status polling for articles that are
 * in pending or processing states. This dramatically reduces the number of
 * API calls compared to polling each article individually.
 */

import { useEffect, useRef, useCallback } from 'react';
import { createExponentialBackoff } from '../utils/backoff';
import { useArticles } from './useArticles';

/**
 * Hook to manage batch status polling for pending/processing articles
 * @param {number} intervalMs - Polling interval in milliseconds (default: 3000)
 * @param {boolean} enabled - Whether polling is enabled (default: true)
 */
export const useBatchStatusPolling = (intervalMs = 3000, enabled = true) => {
  const { 
    articles, 
    checkBatchStatus, 
    updateArticlesWithBatchStatus 
  } = useArticles();
  
  const isRunningRef = useRef(false);
  const lastPolledRef = useRef(new Set());
  const backoffRef = useRef(createExponentialBackoff({ initialMs: intervalMs, maxMs: 180000 }));
  const SUCCESS_DELAY_MS = 15000; // Always wait 15s after successful poll
  const runIdRef = useRef(0);
  const timeoutRef = useRef(null);

  /**
   * Get articles that need status polling
   */
  const getArticlesToPoll = useCallback(() => {
    return articles
      .filter(article => 
        article.processing_status === 'pending' || 
        article.processing_status === 'processing' ||
        article.processing_status === 'retrying'
      )
      .map(article => article.id);
  }, [articles]);

  /**
   * Perform batch status check and update local state
   */
  const pollBatchStatus = useCallback(async () => {
    const articleIds = getArticlesToPoll();
    
    if (articleIds.length === 0) {
      // No articles to poll, stop the runner
      isRunningRef.current = false;
      lastPolledRef.current.clear();
      return;
    }

    try {
      // Only poll if the set of articles has changed or if we haven't polled recently
      const currentIds = new Set(articleIds);
      const lastPolledIds = lastPolledRef.current;
      
      const hasChanges = currentIds.size !== lastPolledIds.size || 
        [...currentIds].some(id => !lastPolledIds.has(id));
      
      if (hasChanges || articleIds.length > 0) {
        const statusMap = await checkBatchStatus(articleIds);
        updateArticlesWithBatchStatus(statusMap);
        lastPolledRef.current = currentIds;
      }
    } catch (error) {
      console.warn('Batch status polling failed:', error);
      // Continue polling even on errors, but don't throw
    }
  }, [getArticlesToPoll, checkBatchStatus, updateArticlesWithBatchStatus]);

  /**
   * Start sequential polling with exponential backoff.
   */
  const startPolling = useCallback(() => {
    if (isRunningRef.current || !enabled) return;
    isRunningRef.current = true;

    // Reset backoff on start or interval change
    backoffRef.current = createExponentialBackoff({ initialMs: intervalMs, maxMs: 180000 });
    runIdRef.current += 1;
    const myRunId = runIdRef.current;

    const scheduleNext = (delayMs) => {
      // Clear any existing timer before scheduling
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(async () => {
        // If stopped or superseded, do nothing
        if (!isRunningRef.current || myRunId !== runIdRef.current) return;

        const ids = getArticlesToPoll();
        if (ids.length === 0) {
          isRunningRef.current = false;
          lastPolledRef.current.clear();
          return;
        }

        let wasError = false;
        try {
          await pollBatchStatus();
        } catch (e) {
          wasError = true;
        }

        if (!isRunningRef.current || myRunId !== runIdRef.current) return;

        const stillHasArticles = getArticlesToPoll().length > 0;
        if (!stillHasArticles) {
          isRunningRef.current = false;
          return;
        }

        if (wasError) {
          const next = backoffRef.current.nextErrorDelay();
          scheduleNext(next);
        } else {
          backoffRef.current.reset();
          scheduleNext(SUCCESS_DELAY_MS);
        }
      }, delayMs);
    };

    // Initial delay before first poll
    scheduleNext(SUCCESS_DELAY_MS);
  }, [enabled, intervalMs, getArticlesToPoll, pollBatchStatus]);

  /**
   * Stop batch status polling
   */
  const stopPolling = useCallback(() => {
    isRunningRef.current = false;
    runIdRef.current += 1; // invalidate any in-flight loop iterations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    lastPolledRef.current.clear();
  }, []);

  /**
   * Restart polling (useful when interval changes)
   */
  const restartPolling = useCallback(() => {
    stopPolling();
    if (enabled) {
      startPolling();
    }
  }, [stopPolling, startPolling, enabled]);

  // Start polling when component mounts or when dependencies change
  useEffect(() => {
    if (enabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [enabled, startPolling, stopPolling]);

  // Restart polling when interval changes
  useEffect(() => {
    if (enabled) {
      restartPolling();
    }
  }, [intervalMs, restartPolling, enabled]);

  // Handle articles changing - start/stop polling as needed
  useEffect(() => {
    const articleIds = getArticlesToPoll();
    
    if (articleIds.length > 0 && enabled && !isRunningRef.current) {
      startPolling();
    } else if (articleIds.length === 0 && isRunningRef.current) {
      stopPolling();
    }
  }, [articles, getArticlesToPoll, enabled, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isRunningRef.current = false;
      runIdRef.current += 1;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isPolling: !!isRunningRef.current,
    articlesToPoll: getArticlesToPoll(),
    startPolling,
    stopPolling,
    restartPolling,
    pollBatchStatus, // For manual polling
  };
};